
import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
    makeStyles,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
} from "@material-ui/core";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/pt-br";
import "moment/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { toast } from "react-toastify";
import { openApi } from "../../services/api";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// [ANTIGRAVITY] Fijar locale ES ANTES de crear el localizer para que
// la semana empiece en Lunes y los eventos se ubiquen en la columna correcta
moment.locale("es");
const localizer = momentLocalizer(moment);

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: theme.spacing(4),
        minHeight: "100vh", // Changed from height to minHeight
        backgroundColor: theme.palette.background.default,
        overflowY: "auto", // Allow scrolling
    },
    calendarPaper: {
        padding: theme.spacing(2),
        height: "auto", // Let it grow
        width: "90%",
        maxWidth: "1000px",
        marginBottom: theme.spacing(4),
    },
    title: {
        marginBottom: theme.spacing(2),
    },
    // [ANTIGRAVITY] Hide "Today" button from navigation group
    hideToday: {
        "& .rbc-btn-group:first-child button:first-child": {
            display: "none",
        },
    },
}));

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const PublicBooking = () => {
    const classes = useStyles();
    const { companyId } = useParams();
    const query = useQuery();

    // Params from URL
    const serviceId = query.get("serviceId");
    const professionalId = query.get("professionalId");
    const contactId = query.get("contactId");

    const [events, setEvents] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [contact, setContact] = useState({ name: "", email: "", phone: "" });
    const [loading, setLoading] = useState(false);
    const [viewDate, setViewDate] = useState(new Date()); // Track current view date
    const [view, setView] = useState('month'); // [ANTIGRAVITY] Default to month view

    useEffect(() => {
        // [ANTIGRAVITY] moment.locale ya fijado a nivel de módulo
    }, []);

    useEffect(() => {
        if (contactId) {
            // Optionally fetch contact info if we have an endpoint for public contact info?
            // Usually not public. We presume user knows who they are.
            // Or we can assume params might carry name/email too? No, security risk.
            // We just ask user to confirm details.
        }
    }, [contactId]);

    const fetchAvailability = async (date) => {
        if (!professionalId || !serviceId || !companyId) return;

        // [ANTIGRAVITY] Cubrir la cuadrícula completa del mes: incluye días del mes
        // anterior y siguiente que son visibles (ej: Mayo 1-3 en la última fila de Abril).
        const startDate = view === 'month'
            ? moment(date).startOf('month').startOf('isoWeek').format("YYYY-MM-DD")
            : moment(date).startOf('isoWeek').format("YYYY-MM-DD");
        const endDate = view === 'month'
            ? moment(date).endOf('month').endOf('isoWeek').format("YYYY-MM-DD")
            : moment(date).endOf('isoWeek').format("YYYY-MM-DD");

        try {
            const { data } = await openApi.get("/public/appointments/availability", {
                params: {
                    professionalId,
                    serviceId,
                    startDate,
                    endDate,
                    companyId,
                    contactId
                }
            });

            // Transform availability to calendar events
            const newEvents = [];

            data.forEach(day => {
                const slotsWithStatus = day.slots.map(s => ({
                    time: typeof s === 'string' ? s : s.time,
                    status: typeof s === 'string' ? "available" : s.status
                }));

                if (view === 'month') {
                    // [ANTIGRAVITY] Renderizar slots individuales en lugar de agruparlos
                    // para evitar confusiones al usuario.
                    slotsWithStatus.forEach(slot => {
                        const startInfo = moment(`${day.date} ${slot.time}`, "YYYY-MM-DD HH:mm");
                        const isBusy = slot.status === "busy";
                        
                        const isMine = slot.status === "expired_mine" || slot.status === "busy_mine";
                        const timeRange = `${slot.time} - ${startInfo.clone().add(30, 'minutes').format("HH:mm")}`;
                        const titleText = isMine ? `🔴 ${timeRange}` : timeRange;

                        newEvents.push({
                            title: titleText,
                            start: startInfo.toDate(),
                            end: startInfo.clone().add(30, "minutes").toDate(),
                            allDay: true,
                            resource: { date: day.date, time: slot.time, status: slot.status, type: 'individual' }
                        });
                    });
                } else {
                    // Detailed View (Week/Day) - Show individual slots
                    slotsWithStatus.forEach(slot => {
                        const startInfo = moment(`${day.date} ${slot.time}`, "YYYY-MM-DD HH:mm");
                        const isBusy = slot.status === "busy";

                        const isMine = slot.status === "expired_mine" || slot.status === "busy_mine";
                        
                        let titleText = isBusy ? "Reservado" : (slot.status === "expired_mine" || slot.status === "expired" ? "Expirado" : (i18n.t("publicBooking.available") || "Disponible"));
                        if (isMine) titleText = `🔴 ${titleText}`;

                        newEvents.push({
                            title: titleText,
                            start: startInfo.toDate(),
                            end: startInfo.add(30, "minutes").toDate(),
                            allDay: false,
                            resource: { date: day.date, time: slot.time, status: slot.status }
                        });
                    });
                }
            });
            setEvents(newEvents);

        } catch (err) {
            toastError(err);
        }
    };

    useEffect(() => {
        fetchAvailability(viewDate);
        // eslint-disable-next-line
    }, [professionalId, serviceId, companyId, view, viewDate]); // Re-fetch when view changes

    const handleSelectEvent = (event) => {
        const { status } = event.resource;
        if (status === "busy" || status === "expired" || status === "busy_mine" || status === "expired_mine") return; // Block click on booked/expired slots
        setSelectedSlot(event);
        setOpenModal(true);
    };

    const handleSelectSlot = (slotInfo) => {
        // [ANTIGRAVITY] Option 1 implementation: 
        // We no longer drill down to day view automatically to keep the flow focused on visible buttons.
    };

    const eventStyleGetter = (event, start, end, isSelected) => {
        let backgroundColor = "#3174ad"; // Default blue
        let style = {
            borderRadius: "4px",
            opacity: 0.8,
            color: "white",
            border: "0px",
            display: "block"
        };

        if (view === 'month') {
            // Aggregated event style
            if (event.resource.status === 'busy') {
                backgroundColor = "#dc3545"; // Red for busy
                style.cursor = "default";
            } else if (event.resource.status === 'busy_mine') {
                backgroundColor = "#dc3545"; // Same as busy
                style.cursor = "default";
            } else if (event.resource.status === 'expired') {
                backgroundColor = "#fd7e14"; // Orange for expired
                style.cursor = "not-allowed";
                style.opacity = 0.6;
            } else if (event.resource.status === 'expired_mine') {
                backgroundColor = "#fd7e14"; // Same as expired
                style.cursor = "not-allowed";
                style.opacity = 0.9;
                style.fontWeight = "bold";
            } else {
                backgroundColor = "#28a745"; // Green for availability
                style.cursor = "pointer";
            }
            style.borderRadius = "4px";
            style.textAlign = "center";
        } else {
            // Slot style
            style.borderRadius = "0px";
            if (event.resource.status === "available") {
                backgroundColor = "#28a745"; // Bright Green
                style.cursor = "pointer";
            } else if (event.resource.status === "expired") {
                backgroundColor = "#fd7e14"; // Orange
                style.cursor = "not-allowed";
                style.opacity = 0.6;
            } else if (event.resource.status === "expired_mine") {
                backgroundColor = "#fd7e14"; // Same as expired
                style.cursor = "not-allowed";
                style.opacity = 0.9;
                style.fontWeight = "bold";
            } else if (event.resource.status === "busy") {
                backgroundColor = "#dc3545"; // Bright Red
                style.cursor = "not-allowed";
                style.opacity = 0.6;
            } else if (event.resource.status === "busy_mine") {
                backgroundColor = "#dc3545"; // Same as busy
                style.cursor = "not-allowed";
                style.opacity = 0.9;
            }
        }

        return {
            style: {
                ...style,
                backgroundColor
            }
        };
    };

    const handleBook = async () => {
        if (!contact.name || !contact.email || !contact.phone || contact.phone.length < 8) {
            toast.error("Por favor completa todos los campos con información válida, incluyendo tu teléfono.");
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams(window.location.search);
            // Si el link público trae parámetros de recordatorio, se envían, de lo contrario el backend heredará del Service
            const r1v = queryParams.get("r1v") || null;
            const r1u = queryParams.get("r1u") || null;
            const r2v = queryParams.get("r2v") || null;
            const r2u = queryParams.get("r2u") || null;

            await openApi.post("/public/appointments", {
                date: `${selectedSlot.resource.date} ${selectedSlot.resource.time}`,
                professionalId,
                serviceId,
                contactId,
                companyId,
                customer: contact,
                reminders: { r1v, r1u, r2v, r2u }
            });
            toast.success("Cita agendada con éxito! Te hemos enviado un mensaje.");
            setOpenModal(false);
            setContact({ name: "", email: "", phone: "" }); // Clear form
            fetchAvailability(viewDate); // Refresh current view
        } catch (err) {
            toastError(err);
        }
        setLoading(false);
    };

    const handleViewChange = (newView) => {
        setView(newView);
    };

    const handleNavigate = (date) => {
        setViewDate(date);
        // fetchAvailability called by useEffect
    };

    return (
        <div className={classes.root}>
            <Typography variant="h4" className={classes.title}>
                {i18n.t("publicBooking.title") || "Agendar Cita"}
            </Typography>

            {/* Legend - Show for all views */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: 15, height: 15, backgroundColor: '#28a745', borderRadius: 3 }}></div>
                    <Typography variant="body2">Disponible</Typography>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: 15, height: 15, backgroundColor: '#dc3545', borderRadius: 3 }}></div>
                    <Typography variant="body2">Reservado</Typography>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: 15, height: 15, backgroundColor: '#fd7e14', borderRadius: 3 }}></div>
                    <Typography variant="body2">Expirado</Typography>
                </div>
            </div>

            {view !== 'month' && (
                <Button onClick={() => setView('month')} variant="outlined" style={{ marginBottom: 10 }}>
                    Volver a Vista Mensual
                </Button>
            )}

            <Paper className={`${classes.calendarPaper} ${classes.hideToday}`} elevation={3}>
                <Calendar
                    localizer={localizer}
                    culture="es"
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: "700px" }}
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectable
                    view={view}
                    onView={setView}
                    date={viewDate}
                    onNavigate={handleNavigate}
                    views={['month']}
                    toolbar={true}
                    popup
                    popupOffset={{ x: 0, y: 20 }}
                    min={new Date(0, 0, 0, 5, 0, 0)}
                    max={new Date(0, 0, 0, 23, 0, 0)}
                    step={30}
                    timeslots={2}
                    messages={{
                        next: "Siguiente",
                        previous: "Anterior",
                        today: "Hoy",
                        month: "Mes",
                        week: "Semana",
                        day: "Día",
                        showMore: total => `+${total} horarios más`
                    }}
                />
            </Paper>

            <Dialog open={openModal} onClose={() => setOpenModal(false)}>
                <DialogTitle>{i18n.t("publicBooking.confirmTitle") || "Confirmar Reserva"}</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {selectedSlot && `Fecha: ${moment(selectedSlot.start).format("DD/MM/YYYY HH:mm")}`}
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nombre y Apellido"
                        fullWidth
                        value={contact.name}
                        onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Email"
                        type="email"
                        fullWidth
                        value={contact.email}
                        onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    />
                    <div style={{ marginTop: 15 }}>
                        <Typography variant="caption" color="textSecondary" style={{ marginBottom: 5, display: "block" }}>
                            Teléfono (WhatsApp)
                        </Typography>
                        <PhoneInput
                            country={'pe'}
                            value={contact.phone}
                            onChange={(phone) => setContact({ ...contact, phone })}
                            inputStyle={{ width: '100%', height: '40px' }}
                            placeholder="+51 912 345 678"
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)} color="secondary">
                        Cancelar
                    </Button>
                    <Button onClick={handleBook} color="primary" disabled={loading}>
                        {loading ? "Agendando..." : "Confirmar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PublicBooking;
